import { api } from '@xtnd/web/shared/api';

export interface QboWebhookEvent {
  eventId: string;
  tenantId: string;
  companyId: string;
  entityType: string;
  eventType: string;
  entityId: string;
  lastUpdated: string;
  status: 'RECEIVED' | 'PROCESSING' | 'PROCESSED' | 'FAILED' | 'received' | 'processing' | 'processed' | 'failed';
  createdAt: string;
  processedAt?: string;
  error?: string;
  sqsMessageId?: string;
  processingDuration?: number;
  payload: {
    eventNotification: any;
    entity: any;
  };
  retryCount: number;
}

export interface QboWebhookEventStats {
  totalEvents: number;
  byStatus: {
    RECEIVED: number;
    PROCESSING: number;
    PROCESSED: number;
    FAILED: number;
  };
  byEntityType: Record<string, number>;
  byEventType: Record<string, number>;
  recentActivity: {
    last24Hours: number;
    lastHour: number;
  };
}

export interface QboWebhookEventQuery {
  entityType?: string;
  eventType?: string;
  entityId?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
}

export interface QboWebhookEventsResponse {
  data: QboWebhookEvent[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

class QboWebhookEventsApi {
  private readonly baseSource = '/qbo-webhook-events';
  private readonly eventsBase = '/webhook/qbo/events';

  /**
   * Query QBO webhook events with filters and pagination
   */
  async queryWebhookEvents(query: QboWebhookEventQuery): Promise<QboWebhookEventsResponse> {
    try {
      const params = new URLSearchParams();

      // Add query parameters
      if (query.entityType) params.append('entityType', query.entityType);
      if (query.eventType) params.append('eventType', query.eventType);
      if (query.entityId) params.append('entityId', query.entityId);
      if (query.status) params.append('status', query.status);
      if (query.startDate) params.append('startDate', query.startDate);
      if (query.endDate) params.append('endDate', query.endDate);
      if (query.page) params.append('page', query.page.toString());
      if (query.limit) params.append('limit', query.limit.toString());
      if (query.sortField) params.append('sortField', query.sortField);
      if (query.sortDirection) params.append('sortDirection', query.sortDirection);

      return await api.get<QboWebhookEventsResponse>(`${this.baseSource}?${params.toString()}`);
    } catch (error) {
      console.error('❌ [QBO Webhook API] Error fetching events:', error);
      throw error;
    }
  }

  /**
   * Get QBO webhook event statistics
   */
  async getWebhookStats(): Promise<QboWebhookEventStats> {
    try {
      return await api.get<QboWebhookEventStats>(`${this.baseSource}/stats`);
    } catch (error) {
      console.error('❌ [QBO Webhook API] Error fetching stats:', error);
      throw error;
    }
  }

  /**
   * Get a single QBO webhook event by ID
   */
  async getWebhookEvent(eventId: string): Promise<QboWebhookEvent> {
    return api.get<QboWebhookEvent>(`${this.eventsBase}/${eventId}`);
  }

  /**
   * Retry a failed QBO webhook event
   */
  async retryWebhookEvent(eventId: string): Promise<void> {
    await api.post(`${this.eventsBase}/${eventId}/retry`);
  }

  /**
   * Get available entity types for filtering
   */
  getEntityTypes(): string[] {
    return [
      'Bill',
      'Invoice',
      'Payment',
      'Customer',
      'Vendor',
      'Item',
      'Account',
      'Employee',
      'TimeActivity',
      'Purchase',
      'SalesReceipt',
      'CreditMemo',
      'Estimate',
      'JournalEntry',
      'Transfer',
      'Deposit',
      'RefundReceipt',
      'VendorCredit',
      'BillPayment',
    ];
  }

  /**
   * Get available event types for filtering
   */
  getEventTypes(): string[] {
    return ['Create', 'Update', 'Delete', 'Merge', 'Void'];
  }

  /**
   * Get available statuses for filtering
   */
  getStatuses(): Array<{ value: string; label: string; color: string }> {
    return [
      { value: 'received', label: 'Received', color: 'blue' },
      { value: 'processing', label: 'Processing', color: 'yellow' },
      { value: 'processed', label: 'Processed', color: 'green' },
      { value: 'failed', label: 'Failed', color: 'red' },
    ];
  }
}

export const qboWebhookEventsApi = new QboWebhookEventsApi();

